import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Progress,
  Spinner,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { CheckCircleIcon, InfoIcon, TimeIcon, WarningIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useStreamingSTT from "../../hooks/useStreamingSTT";
import PracticeTour from "./PracticeTour";

/**
 * Practice/training question screen.
 *
 * Layout mirrors `QuestionPage.jsx` (the real question page):
 *   - Outer Container (xl/1600px) with a single white card.
 *   - Header strip: heading + meta badges (type, theme) + timer.
 *   - Progress bar.
 *   - Two-column body (split on desktop, stacked on mobile):
 *       left  → gray-50 panel with the question content
 *       right → answer textarea + microphone button
 *   - Footer strip with Verify + Start-the-real-test buttons.
 *
 * The static practice question only carries `Question` text (no context /
 * scoring footer / parameters), so the left panel renders just the question
 * — but the wrapper layout is identical to a real OPEN question.
 */
const SampleQuestionPage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { fallback: false }
  );

  const typeLabelFor = (qType) =>
    t(`evaluation.practice.questionTypes.${qType}`, qType || "");

  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(false);

  // Refs for the onboarding tour — kept at the same DOM nodes as in the
  // matching real-question layout (timer, question card, verify button, mic).
  const timerRef = useRef(null);
  const questionTextRef = useRef(null);
  const verifyRef = useRef(null);
  const micRef = useRef(null);

  // Timer (cosmetic — practice question never auto-submits)
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);

  const {
    isRecording,
    isConnecting,
    interimTranscript,
    error: sttError,
    start: startSTT,
    stop: stopSTT,
  } = useStreamingSTT(uuid);
  const [sttBase, setSttBase] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        // Sync i18n to the candidate's evaluation language so the banner,
        // tour copy and section labels match the test language.
        const [sampleRes, detailRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/sample`
          ),
          axios
            .get(
              `${process.env.REACT_APP_BASE_API_URL}/api/evaluation/${uuid}/detail`
            )
            .catch(() => null),
        ]);
        if (!mounted) return;
        const rawLang = (detailRes?.data?.language || "").toLowerCase();
        if (rawLang.includes("angl") || rawLang.includes("engl")) {
          i18n.changeLanguage("en");
        } else if (rawLang.includes("fran") || rawLang.includes("franc")) {
          i18n.changeLanguage("fr");
        }
        setQuestion(sampleRes.data);
        const tSec = Number(sampleRes.data?.responseTime) || 120;
        setTotalTime(tSec);
        setTimeLeft(tSec);
        setLoading(false);
      } catch {
        if (mounted) {
          setFetchError(true);
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [uuid, i18n]);

  // Cosmetic countdown
  useEffect(() => {
    if (!question || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [question, timeLeft]);

  // Append final STT transcripts to the textarea
  useEffect(() => {
    if (isRecording) return;
    const segment = (interimTranscript || "").trim();
    if (segment) {
      const next = sttBase ? `${sttBase} ${segment}` : segment;
      setAnswer(next);
      setSttBase(next);
    }
  }, [isRecording, interimTranscript, sttBase]);

  const toggleMic = () => {
    if (isRecording) {
      stopSTT();
    } else {
      setSttBase(answer);
      startSTT();
    }
  };

  const submitPreview = async () => {
    if (!question || !answer.trim()) return;
    setSubmitting(true);
    setPreviewError(false);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions/${question.questionId}/preview`,
        { user_response: answer }
      );
      setPreview(res.data);
    } catch {
      setPreviewError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const startTest = () => navigate(`/evaluate/${uuid}/start`);

  const formatTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={3}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Spinner size="xl" color="purple.500" thickness="4px" />
          </Center>
        </Container>
      </Box>
    );
  }

  if (fetchError || !question) {
    return (
      <Box minH="100vh" bg="gray.50" py={3}>
        <Container maxW="container.md">
          <Alert status="warning" borderRadius="md" data-testid="sample-fetch-error">
            <AlertIcon />
            <Text>
              {t(
                "evaluation.practice.fetchError",
                "We couldn't load the practice question. You can start the real test directly."
              )}
            </Text>
          </Alert>
          <Button mt={4} colorScheme="blue" onClick={startTest}>
            {t("evaluation.practice.startTest", "Start the real test")}
          </Button>
        </Container>
      </Box>
    );
  }

  const progressPct = totalTime > 0 ? (timeLeft / totalTime) * 100 : 0;
  const typeLabel = typeLabelFor(question.questionType);
  const practiceHeading = t(
    "evaluation.practice.heading",
    "Practice question"
  );

  return (
    <Box minH="100vh" bg="gray.50" py={3}>
      <Container maxW={{ base: "container.xl", lg: "1600px" }}>
        {/* Practice banner — sits above the main question card */}
        <Alert
          status="info"
          variant="left-accent"
          borderRadius="md"
          mb={4}
          fontSize="md"
          data-testid="practice-banner"
        >
          <AlertIcon boxSize={5} />
          <Text fontWeight="medium" sx={{ marginBottom: "0 !important" }}>
            {t(
              "evaluation.practice.banner",
              "Practice question — it does not count toward your final score. Use it to get familiar with the interface."
            )}
          </Text>
        </Alert>

        <Box bg="white" borderRadius="lg" borderWidth="1px" shadow="sm">
          {/* Header */}
          <Box p={4} borderBottomWidth="1px">
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" align="flex-start" flexWrap="wrap">
                <Heading size="lg" color="black" sx={{ marginBottom: "0 !important" }}>
                  {practiceHeading}
                </Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <Badge
                    colorScheme="purple"
                    fontSize="md"
                    px={4}
                    py={2}
                    borderRadius="md"
                  >
                    {typeLabel}
                  </Badge>
                  {question.theme && (
                    <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                      {question.theme}
                    </Badge>
                  )}
                  <HStack spacing={2} color="gray.700" ref={timerRef}>
                    <TimeIcon />
                    <Text
                      fontWeight="medium"
                      fontSize="md"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {formatTime(timeLeft)} / {formatTime(totalTime)}
                    </Text>
                  </HStack>
                </HStack>
              </HStack>

              <Box position="relative">
                <Progress
                  value={progressPct}
                  size="md"
                  colorScheme={progressPct > 20 ? "purple" : "red"}
                  borderRadius="full"
                />
                {progressPct <= 20 && (
                  <HStack spacing={1} mt={1} justify="center">
                    <WarningIcon color="red.500" boxSize="14px" />
                    <Text
                      fontSize="sm"
                      color="red.600"
                      fontWeight="semibold"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {t("evaluation.timeRunningLow", "Time running low")}
                    </Text>
                  </HStack>
                )}
              </Box>
            </VStack>
          </Box>

          {/* Question + Answer split */}
          <Grid templateColumns={isDesktop ? "1fr 1fr" : "1fr"} gap={0}>
            {/* Left: Question content */}
            <GridItem
              p={4}
              bg="gray.50"
              borderRightWidth={isDesktop ? "1px" : "0"}
              borderBottomWidth={isDesktop ? "0" : "1px"}
              ref={questionTextRef}
            >
              <VStack spacing={3} align="stretch">
                <Heading size="md" sx={{ marginBottom: "0 !important" }}>
                  {practiceHeading}
                </Heading>
                <Box>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.500"
                    mb={1}
                    sx={{ marginBottom: "4px !important" }}
                  >
                    {t("evaluation.practice.sectionQuestion", "Question")}
                  </Text>
                  <Text fontSize="md">
                    {question.questionMain ||
                      question.questionText ||
                      ""}
                  </Text>
                </Box>
              </VStack>
            </GridItem>

            {/* Right: Answer */}
            <GridItem
              p={4}
              display="flex"
              flexDirection="column"
            >
              <VStack
                spacing={3}
                align="stretch"
                flex={isDesktop ? 1 : undefined}
              >
                <HStack spacing={2}>
                  <CheckCircleIcon color="green.500" />
                  <Heading size="md" sx={{ marginBottom: "0 !important" }}>
                    {t("evaluation.practice.yourAnswer", "Your answer")}
                  </Heading>
                </HStack>

                <Textarea
                  value={
                    isRecording && interimTranscript
                      ? `${sttBase ? sttBase + " " : ""}${interimTranscript}`
                      : answer
                  }
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={t(
                    "evaluation.practice.answerPlaceholder",
                    "Type your answer or use the microphone..."
                  )}
                  rows={6}
                  resize="vertical"
                  flex={isDesktop ? 1 : undefined}
                  minH={isDesktop ? "220px" : "180px"}
                  bg="white"
                  borderColor="gray.300"
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                  }}
                />

                <HStack ref={micRef} spacing={2}>
                  <Tooltip
                    label={
                      isRecording
                        ? t(
                            "evaluation.practice.stopMic",
                            "Stop dictation"
                          )
                        : t(
                            "evaluation.practice.startMic",
                            "Dictate via microphone"
                          )
                    }
                  >
                    <Button
                      size="md"
                      bg={isRecording ? "red.500" : "rgb(37, 87, 167)"}
                      color="white"
                      _hover={{
                        bg: isRecording ? "red.600" : "rgb(27, 67, 137)",
                      }}
                      leftIcon={
                        isConnecting ? (
                          <Spinner size="sm" />
                        ) : (
                          <Box
                            as="i"
                            className={`fa-solid ${isRecording ? "fa-stop" : "fa-microphone"}`}
                          />
                        )
                      }
                      onClick={toggleMic}
                    >
                      {isRecording
                        ? t("evaluation.practice.stopMic", "Stop dictation")
                        : t(
                            "evaluation.practice.startMic",
                            "Dictate via microphone"
                          )}
                    </Button>
                  </Tooltip>
                  {sttError && (
                    <Text color="red.500" fontSize="sm">
                      {sttError}
                    </Text>
                  )}
                </HStack>
              </VStack>
            </GridItem>
          </Grid>

          {/* Footer action strip */}
          <Flex
            p={4}
            borderTopWidth="1px"
            align="center"
            justify="space-between"
            wrap="wrap"
            gap={3}
          >
            <Box ref={verifyRef}>
              <Button
                colorScheme="blue"
                onClick={submitPreview}
                isLoading={submitting}
                isDisabled={!answer.trim()}
                leftIcon={<CheckCircleIcon />}
              >
                {t("evaluation.practice.verify", "Verify my answer")}
              </Button>
            </Box>
            <Button colorScheme="green" onClick={startTest}>
              {t("evaluation.practice.startTest", "Start the real test")}
            </Button>
          </Flex>
        </Box>

        {previewError && (
          <Alert status="error" borderRadius="md" mt={4}>
            <AlertIcon />
            {t(
              "evaluation.practice.previewError",
              "We couldn't evaluate your practice answer. Please try again."
            )}
          </Alert>
        )}

        {preview && (
          <Box
            mt={4}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            p={5}
          >
            <Heading size="sm" sx={{ marginBottom: "8px !important" }}>
              {t("evaluation.practice.resultTitle", "Practice result")}
            </Heading>
            <Stack direction={{ base: "column", md: "row" }} spacing={6}>
              {typeof preview.points === "number" && (
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {t("evaluation.practice.score", "Score")}
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {preview.points}
                    {preview.max_points ? ` / ${preview.max_points}` : ""}
                  </Text>
                </Box>
              )}
              {preview.comment && (
                <Box flex="1">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={1}
                    sx={{ marginBottom: "4px !important" }}
                  >
                    {t("evaluation.practice.comment", "Feedback")}
                  </Text>
                  <Text>{preview.comment}</Text>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        <Flex justify="center" mt={4}>
          <HStack>
            <InfoIcon color="gray.400" />
            <Text fontSize="sm" color="gray.500">
              {t(
                "evaluation.practice.footer",
                "When you're ready, click 'Start the real test' to begin. Practice answers are not saved."
              )}
            </Text>
          </HStack>
        </Flex>
      </Container>

      <PracticeTour
        targets={{
          timer: timerRef,
          questionText: questionTextRef,
          verify: verifyRef,
          microphone: micRef,
        }}
      />
    </Box>
  );
};

export default SampleQuestionPage;
